import React, { useRef, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, Grid, useTheme, useMediaQuery, Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import Header from './Header';
import LeftPanel from './LeftPanel';
import MainPanel from './MainPanel';
import LandingPage from '../shared/components/LandingPage';
import MammoAssistChat from '../components/MammoAssistChat';
import MammoAssistDialog from './LeftPanel/MammoAssistDialog';
import LandingSidebar from './LandingSidebar';
import LandingChatWindow from './LandingChatWindow';

const SIDEBAR_WIDTH = 360;

const AppLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const [leftPanelOpen, setLeftPanelOpen] = useState(!isMobile);
  const mainPanelRef = useRef(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [landingChatOpen, setLandingChatOpen] = useState(false);
  const [landingTopic, setLandingTopic] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState({
    fastapi: 'disconnected',
    mcp: 'disconnected',
    graphql: 'disconnected'
  });

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleOpenChat = () => {
    setDialogOpen(true);
  };

  const handleExploreTopic = (topic) => {
    setLandingTopic(topic);
    setLandingChatOpen(true);
  };

  const getConnectionStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'success.main';
      case 'error': return 'error.main';
      default: return 'grey.400';
    }
  };

  // Show landing page content if user is not authenticated
  if (!user) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {/* Global Header */}
        <Header 
          onMenuToggle={() => setLeftPanelOpen(!leftPanelOpen)}
          isMobile={isMobile}
          isLandingPage={true}
        />
        
        {/* Main Content Layout using Grid with 1:3 ratio */}
        <Grid
          container
          columns={{ xs: 1, md: 4 }}
          sx={{ flexGrow: 1, minHeight: 0, position: 'relative', flexWrap: 'nowrap' }}
        >
          {!isMobile && (
            <Grid
              item
              xs={1}
              md={1}
              component="aside"
              sx={{
                transition: 'max-width 0.3s ease, flex-basis 0.3s ease, opacity 0.2s ease',
                maxWidth: leftPanelOpen ? SIDEBAR_WIDTH : 0,
                flexBasis: leftPanelOpen ? SIDEBAR_WIDTH : 0,
                width: leftPanelOpen ? SIDEBAR_WIDTH : 0,
                minWidth: leftPanelOpen ? SIDEBAR_WIDTH : 0,
                flexShrink: 0,
                overflow: 'hidden',
                borderRight: leftPanelOpen ? 1 : 0,
                borderColor: 'divider',
                backgroundColor: leftPanelOpen ? 'background.paper' : 'transparent',
                minHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
                opacity: leftPanelOpen ? 1 : 0,
                pointerEvents: leftPanelOpen ? 'auto' : 'none',
                position: 'relative'
              }}
            >
              {/* Landing Page Sidebar Content */}
              <LandingSidebar />
            </Grid>
          )}

          {/* Main content area */}
          <Grid 
            item 
            xs={1} 
            md={3} 
            component="main"
            ref={mainPanelRef}
            sx={{ 
              flexGrow: 1, 
              overflow: 'auto',
              minHeight: '100%',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative' // For chat window positioning
            }}
          >
            <LandingPage onExploreTopic={handleExploreTopic} activeTopic={landingTopic} />
            
            {/* Landing Chat Window - positioned relative to main panel */}
            <LandingChatWindow
              isOpen={landingChatOpen}
              onClose={() => setLandingChatOpen(false)}
              onMinimize={() => setLandingChatOpen(false)}
              mainPanelRef={mainPanelRef}
              topicContext={landingTopic}
            />
          </Grid>
        </Grid>

        {/* Global MammoAssist Dialog */}
        <MammoAssistDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          user={user}
          currentPortal="landing"
          connectionStatus={connectionStatus}
          addUserMenuAnchor={null}
          setAddUserMenuAnchor={() => {}}
          addAgentMenuAnchor={null}
          setAddAgentMenuAnchor={() => {}}
          handleAddUser={() => {}}
          handleAddAgent={() => {}}
          getConnectionStatusColor={getConnectionStatusColor}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Global Header */}
      <Header 
        onMenuToggle={() => setLeftPanelOpen(!leftPanelOpen)}
        isMobile={isMobile}
      />
      
      {/* Main Content Layout using Grid with 1:3 ratio */}
      <Grid
        container
        columns={{ xs: 1, md: 4 }}
        sx={{ flexGrow: 1, minHeight: 0, position: 'relative', flexWrap: 'nowrap' }}
      >
        {!isMobile && (
          <Grid
            item
            xs={1}
            md={1}
            component="aside"
            sx={{
              transition: 'max-width 0.3s ease, flex-basis 0.3s ease, opacity 0.2s ease',
              maxWidth: leftPanelOpen ? SIDEBAR_WIDTH : 0,
              flexBasis: leftPanelOpen ? SIDEBAR_WIDTH : 0,
              width: leftPanelOpen ? SIDEBAR_WIDTH : 0,
              minWidth: leftPanelOpen ? SIDEBAR_WIDTH : 0,
              flexShrink: 0,
              overflow: 'hidden',
              borderRight: leftPanelOpen ? 1 : 0,
              borderColor: 'divider',
              backgroundColor: leftPanelOpen ? 'background.paper' : 'transparent',
              minHeight: '100%',
              display: 'flex',
              flexDirection: 'column',
              opacity: leftPanelOpen ? 1 : 0,
              pointerEvents: leftPanelOpen ? 'auto' : 'none',
              position: 'relative'
            }}
          >
            
            <Routes>
              <Route path="/profile" element={null} />
              <Route
                path="*"
                element={
                  <LeftPanel
                    open={leftPanelOpen}
                    onClose={() => setLeftPanelOpen(false)}
                    isMobile={isMobile}
                    onOpenChat={() => setChatOpen(true)}
                  />
                }
              />
            </Routes>
          </Grid>
        )}

        <Grid
          item
          xs={1}
          md={3}
          component="main"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            minHeight: '100%',
            position: 'relative'
          }}
        >
          <Routes>
            <Route path="/patient/*" element={<Navigate to="/dashboard" replace />} />
            <Route path="/clinical/*" element={<Navigate to="/dashboard" replace />} />
            <Route path="/community/*" element={<Navigate to="/dashboard" replace />} />
            <Route path="/admin/*" element={<Navigate to="/dashboard" replace />} />
            <Route path="/profile" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<MainPanel user={user} onOpenChat={() => setChatOpen(true)} />} />
          </Routes>
        </Grid>
      </Grid>

      {/* Mobile left panel drawer */}
      {isMobile && (
        <Routes>
          <Route
            path="*"
            element={
              <LeftPanel
                open={leftPanelOpen}
                onClose={() => setLeftPanelOpen(false)}
                isMobile={isMobile}
                onOpenChat={() => setChatOpen(true)}
              />
            }
          />
        </Routes>
      )}

      {/* Global MammoAssist Chat Dialog */}
      <Dialog
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '80vh', maxHeight: 720 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          MammoAssist AI Chat
          <IconButton onClick={() => setChatOpen(false)} size="small" aria-label="Close chat">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '100%', overflow: 'hidden' }}>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <MammoAssistChat userRole={user?.role} portalContext="global" compact={false} />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AppLayout;